import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { 
    IoArrowBack, 
    IoChatboxOutline, 
    IoTimeOutline, 
    IoCheckmarkCircleOutline, 
    IoCloseCircleOutline, 
    IoEyeOutline, 
    IoWater, 
    IoFlash, 
    IoHammer, 
    IoLeaf, 
    IoBug, 
    IoBuild, 
    IoColorPalette, 
    IoHome, 
    IoKey, 
    IoHappy, 
    IoCar, 
    IoEllipsisHorizontalCircle,
    IoStorefrontOutline
} from 'react-icons/io5';
import moment from 'moment';
import './MisCotizacionesScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

// Helper: Icono por Categoría (Prioridad) o Tipo de Servicio
const getServiceIcon = (categoria, tipo) => {
    const cat = (categoria || '').toLowerCase();

    if (cat.includes('plomería') || cat.includes('plomeria')) return <IoWater />;
    if (cat.includes('electricidad')) return <IoFlash />;
    if (cat.includes('carpintería') || cat.includes('carpinteria')) return <IoHammer />;
    if (cat.includes('jardinería') || cat.includes('jardineria')) return <IoLeaf />;
    if (cat.includes('fumigación') || cat.includes('fumigacion')) return <IoBug />;
    if (cat.includes('pintura')) return <IoColorPalette />;
    if (cat.includes('albañilería') || cat.includes('albañileria') || cat.includes('construccion')) return <IoBuild />;
    if (cat.includes('limpieza') || cat.includes('doméstico') || cat.includes('domestico')) return <IoHome />;
    if (cat.includes('cerrajería') || cat.includes('cerrajeria')) return <IoKey />;
    if (cat.includes('mudanzas')) return <IoCar />;
    if (cat.includes('niñera')) return <IoHappy />;

    const t = (tipo || '').toLowerCase();
    if (t.includes('plomer')) return <IoWater />;
    if (t.includes('electri')) return <IoFlash />;
    return <IoEllipsisHorizontalCircle />; // Default final
};

// Helper: Avatar del Proveedor (Iniciales)
const ProviderAvatar = ({ nombre }) => {
    const initials = (nombre || 'P').substring(0, 2).toUpperCase();
    return (
        <div className="provider-avatar">
            {initials}
        </div>
    );
};

function MisCotizacionesScreen() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [cotizaciones, setCotizaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('activos'); // 'activos' o 'historial'

    // Fixed banner gradient
    const gradient = 'linear-gradient(135deg, #6348D1 0%, #4f46e5 100%)';

    useEffect(() => {
        if (!currentUser) return;

        setLoading(true);
        const q = query(
            collection(db, "cotizaciones"),
            where("residenteId", "==", currentUser.uid),
            orderBy("fechaCreacion", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const listaCotizaciones = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setCotizaciones(listaCotizaciones);
            setLoading(false);
        }, (error) => {
            console.error("Error al cargar cotizaciones: ", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // Helper: Status Pill Logic
    const getStatusBadge = (estatus) => {
        let className = 'status-pill';
        let icon = <IoTimeOutline />;
        let text = estatus;

        if (estatus === 'pendiente' || estatus === 'pendiente_proveedor') {
            className += ' pendiente';
            text = 'Esperando Proveedor';
        } else if (estatus === 'pendiente_residente') {
            className += ' en-proceso';
            icon = <IoChatboxOutline />;
            text = 'Respuesta Recibida';
        } else if (estatus === 'aceptada' || estatus === 'servicio_programado') {
            className += ' aceptada';
            icon = <IoCheckmarkCircleOutline />;
            text = 'Programado';
        } else if (estatus === 'proveedor_en_camino') {
            className += ' en-proceso';
            icon = <IoTimeOutline />;
            text = 'En Camino';
        } else if (estatus === 'proveedor_en_sitio') {
            className += ' en-proceso';
            icon = <IoTimeOutline />;
            text = 'En Sitio (Presupuestando)';
        } else if (estatus === 'esperando_aprobacion_final') {
            className += ' en-proceso';
            icon = <IoChatboxOutline />;
            text = 'Aprobar Presupuesto';
        } else if (estatus === 'servicio_en_curso') {
            className += ' en-proceso';
            icon = <IoTimeOutline />;
            text = 'En Curso';
        } else if (estatus === 'servicio_terminado' || estatus === 'completada' || estatus === 'completado') {
            className += ' completada';
            icon = <IoCheckmarkCircleOutline />;
            text = 'Terminado';
        } else if (estatus === 'rechazada' || estatus === 'cancelada' || estatus === 'cancelado_con_cobro') {
            className += ' rechazada';
            icon = <IoCloseCircleOutline />;
            text = 'Cancelado';
        }

        return (
            <span className={className}>
                {icon} {text}
            </span>
        );
    };

    // Filter services based on activeTab
    const filteredCotizaciones = cotizaciones.filter(coti => {
        const isFinished = ['completada', 'completado', 'servicio_terminado', 'rechazada', 'cancelada', 'cancelado_con_cobro'].includes(coti.estatus);
        return activeTab === 'activos' ? !isFinished : isFinished;
    });

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/marketplace" />
            
            <main className="ks-main">
                <KondoBrandHeader showBack={false} />

                <div className="ks-scroll-area">
                    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
                        
                        {/* Premium Fixed Header Banner - Indigo Gradient */}
                        <div style={{
                            background: gradient,
                            position: 'relative',
                            overflow: 'hidden',
                            padding: '30px 25px',
                            borderRadius: '24px',
                            marginBottom: '30px',
                            boxShadow: '0 12px 30px rgba(99, 72, 209, 0.15)',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', zIndex: 2, position: 'relative' }}>
                                <button
                                    type="button"
                                    onClick={() => navigate('/marketplace')}
                                    style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        borderRadius: '50%',
                                        width: '44px',
                                        height: '44px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        color: 'white',
                                        flexShrink: 0
                                    }}
                                    className="encuestas-glass-btn"
                                >
                                    <IoArrowBack size={22} />
                                </button>
                                <div>
                                    <h1 style={{ color: 'white', margin: '0 0 6px', fontSize: '1.7rem', fontWeight: '800', fontFamily: "'Manrope', sans-serif" }}>
                                        Mis Servicios Contratados
                                    </h1>
                                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.95rem', fontWeight: '500' }}>
                                        Seguimiento de cotizaciones y servicios activos o pasados
                                    </p>
                                </div>
                            </div>
                            <div style={{ position: 'absolute', right: '-20px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.12 }}>
                                <IoStorefrontOutline size={180} color="white" />
                            </div>
                        </div>

                        {/* Tabs Selector */}
                        <div style={{
                            display: 'flex',
                            background: 'white',
                            padding: '6px',
                            borderRadius: '16px',
                            marginBottom: '24px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                        }}>
                            <button
                                onClick={() => setActiveTab('activos')}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: activeTab === 'activos' ? '#6348D1' : 'transparent',
                                    color: activeTab === 'activos' ? 'white' : '#64748b',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontSize: '0.95rem'
                                }}
                            >
                                Activos
                            </button>
                            <button
                                onClick={() => setActiveTab('historial')}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: activeTab === 'historial' ? '#6348D1' : 'transparent',
                                    color: activeTab === 'historial' ? 'white' : '#64748b',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    fontSize: '0.95rem'
                                }}
                            >
                                Historial
                            </button>
                        </div>

                        {loading && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                <p>Cargando cotizaciones...</p>
                            </div>
                        )}

                        {!loading && filteredCotizaciones.length === 0 && (
                            <div style={{
                                background: 'white',
                                padding: '40px 20px',
                                borderRadius: '24px',
                                border: '1px solid #f1f5f9',
                                textAlign: 'center',
                                color: '#64748b',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)'
                            }}>
                                <IoChatboxOutline size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
                                <p style={{ margin: 0, fontWeight: '500' }}>
                                    {activeTab === 'activos' 
                                        ? 'No tienes ninguna solicitud o servicio en curso actualmente.' 
                                        : 'No hay servicios finalizados en tu historial.'}
                                </p>
                            </div>
                        )}

                        {!loading && filteredCotizaciones.length > 0 && (
                            <div>
                                {/* Desktop Table View */}
                                <div className="desktop-table-view table-responsive" style={{
                                    background: 'white',
                                    borderRadius: '24px',
                                    border: '1px solid #f1f5f9',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                    overflow: 'hidden'
                                }}>
                                    <table className="modern-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #f1f5f9', background: '#f8fafc' }}>
                                                <th style={{ padding: '14px 18px', fontSize: '0.8rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>Servicio</th>
                                                <th style={{ padding: '14px 18px', fontSize: '0.8rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>Proveedor</th>
                                                <th style={{ padding: '14px 18px', fontSize: '0.8rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>Fecha</th>
                                                <th style={{ padding: '14px 18px', fontSize: '0.8rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>Estatus</th>
                                                <th style={{ padding: '14px 18px', fontSize: '0.8rem', textTransform: 'uppercase', color: '#64748b', fontWeight: '700', textAlign: 'right' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCotizaciones.map((coti) => (
                                                <tr key={coti.id} onClick={() => navigate(`/marketplace/cotizacion/${coti.id}`)} style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }} className="table-row-hover">
                                                    <td style={{ padding: '16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700', color: '#0f172a' }}>
                                                            <div style={{
                                                                width: '32px', height: '32px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center', color: '#6348D1'
                                                            }}>
                                                                {getServiceIcon(coti.categoria, coti.tipoServicio)}
                                                            </div>
                                                            {coti.categoria || coti.tipoServicio}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '16px' }}>
                                                        <span style={{ fontWeight: '600', color: '#334155' }}>{coti.nombreProveedor}</span>
                                                    </td>
                                                    <td style={{ padding: '16px', color: '#64748b', fontSize: '0.85rem' }}>
                                                        {coti.fechaCreacion ? moment(coti.fechaCreacion.toDate()).format('D MMM YYYY') : '-'}
                                                    </td>
                                                    <td style={{ padding: '16px' }}>
                                                        {getStatusBadge(coti.estatus)}
                                                    </td>
                                                    <td style={{ padding: '16px', textAlign: 'right' }}>
                                                        <button style={{
                                                            background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: '#6348D1'
                                                        }}>
                                                            <IoEyeOutline size={18} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards View */}
                                <div className="incidencias-mobile-list" style={{ display: 'none', flexDirection: 'column', gap: '16px' }}>
                                    {filteredCotizaciones.map((coti) => (
                                        <div 
                                            key={coti.id} 
                                            onClick={() => navigate(`/marketplace/cotizacion/${coti.id}`)}
                                            style={{
                                                background: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '20px',
                                                padding: '20px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '14px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>
                                                    <div style={{
                                                        width: '32px', height: '32px', borderRadius: '8px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6348D1', flexShrink: 0
                                                    }}>
                                                        {getServiceIcon(coti.categoria, coti.tipoServicio)}
                                                    </div>
                                                    {coti.categoria || coti.tipoServicio}
                                                </div>
                                                {getStatusBadge(coti.estatus)}
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '12px 16px', borderRadius: '12px' }}>
                                                <div>
                                                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Proveedor</span>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>{coti.nombreProveedor}</span>
                                                </div>
                                                <div>
                                                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Fecha</span>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>{coti.fechaCreacion ? moment(coti.fechaCreacion.toDate()).format('D MMM YYYY') : '-'}</span>
                                                </div>
                                            </div>

                                            {coti.descripcion && (
                                                <div style={{ fontSize: '0.85rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {coti.descripcion}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
                
                <KondoBottomNav activeRoute="/marketplace" />
            </main>
        </div>
    );
}

export default MisCotizacionesScreen;